import React, { useState, useEffect } from 'react';
import { BankStatements } from '../components/bank/BankStatements';
import { FacsimileCard } from '../components/billing/Facsimile';
import Analysis from './Analysis';
import { useSearchParams } from 'react-router-dom';
import { Building2, Save, UserPlus, Trash2, Shield, User, Plus, CheckCircle, Wallet } from 'lucide-react';
import { useData, useAuth } from '../App';
import { Button, Card, Input, Select, Modal, Badge } from '../components/UI';
import { UserRole, BankAccount, CompanyProfile, InjuriesRate, VatMode, VatModeSetting } from '../types';
import { formatDay, formatMoney, orgDisplayName } from '../utils/format';
import { DEFAULT_INJURIES_RATE, VAT_MODE_LABELS } from '../utils/taxLogic';
import { OrgHr } from '../components/directory/OrgHr';
import { confirmDialog } from '../components/Dialogs';

const InjuriesRatesEditor = ({ rates, onChange }: { rates: InjuriesRate[], onChange: (r: InjuriesRate[]) => void }) => {
    const update = (i: number, patch: Partial<InjuriesRate>) => onChange(rates.map((r, j) => j === i ? { ...r, ...patch } : r));
    const add = () => {
        const year = rates.length ? Math.max(...rates.map(r => r.year)) + 1 : new Date().getFullYear();
        onChange([...rates, { year, ratePercent: rates.length ? rates[rates.length - 1].ratePercent : DEFAULT_INJURIES_RATE }]);
    };
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">Тариф на травматизм по годам</label>
                <button type="button" onClick={add} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><Plus size={14}/> Добавить год</button>
            </div>
            <p className="text-xs text-slate-500">
                Из уведомления СФР о страховом тарифе. Ставка действует с указанного года до следующей записи; без записей — {DEFAULT_INJURIES_RATE}%.
            </p>
            {rates.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-slate-500">с</span>
                    <input type="number" min={2000} max={2100} value={r.year}
                        onChange={e => update(i, { year: Number(e.target.value) })}
                        className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                    <span className="text-sm text-slate-500">года</span>
                    <input type="number" min={0} max={11.9} step={0.01} value={r.ratePercent}
                        onChange={e => update(i, { ratePercent: Number(e.target.value) })}
                        className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                    <span className="text-sm text-slate-500">%</span>
                    <button type="button" onClick={() => onChange(rates.filter((_, j) => j !== i))}
                        className="text-slate-400 hover:text-red-600" title="Удалить"><Trash2 size={16}/></button>
                </div>
            ))}
        </div>
    );
};

const VatModesEditor = ({ modes, onChange }: { modes: VatModeSetting[], onChange: (m: VatModeSetting[]) => void }) => {
    const update = (i: number, patch: Partial<VatModeSetting>) => onChange(modes.map((m, j) => j === i ? { ...m, ...patch } : m));
    const add = () => {
        const year = modes.length ? Math.max(...modes.map(m => m.year)) + 1 : new Date().getFullYear();
        onChange([...modes, { year, mode: modes.length ? modes[modes.length - 1].mode : 'USN_5' }]);
    };
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-slate-700">НДС на УСН по годам</label>
                <button type="button" onClick={add} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"><Plus size={14}/> Добавить год</button>
            </div>
            <p className="text-xs text-slate-500">
                Действует с указанного года до следующей записи; без записей — освобождение.
                В 2026 г. освобождение — если доход за 2025 г. не больше 20 млн ₽; 5% — до 272,5 млн ₽.
            </p>
            {modes.map((m, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-slate-500">с</span>
                    <input type="number" min={2000} max={2100} value={m.year}
                        onChange={e => update(i, { year: Number(e.target.value) })}
                        className="w-24 rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                    <span className="text-sm text-slate-500">года</span>
                    <select value={m.mode} onChange={e => update(i, { mode: e.target.value as VatMode })}
                        className="min-w-0 max-w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
                        {(Object.keys(VAT_MODE_LABELS) as VatMode[]).map(k => <option key={k} value={k}>{VAT_MODE_LABELS[k]}</option>)}
                    </select>
                    <button type="button" onClick={() => onChange(modes.filter((_, j) => j !== i))}
                        className="text-slate-400 hover:text-red-600" title="Удалить"><Trash2 size={16}/></button>
                </div>
            ))}
        </div>
    );
};

// --- ВАЖНО: Компонент вынесен НАРУЖУ, чтобы не терялся фокус при вводе ---
const OrgFields = ({ data, onChange, isEditing }: { data: any, onChange: (d: any) => void, isEditing: boolean }) => (
    <div className="space-y-4">
        <Select 
          label="Вид организации" 
          value={data.type} 
          onChange={e => onChange({...data, type: e.target.value})}
          options={[
              { value: 'LEGAL_ENTITY', label: 'Юридическое лицо (ООО)' },
              { value: 'ENTREPRENEUR', label: 'Индивидуальный предприниматель (ИП)' }
          ]}
        />

        <Input 
          label={data.type === 'LEGAL_ENTITY' ? "Название организации" : "ФИО предпринимателя (без «ИП» — добавится в документах)"} 
          value={data.name} 
          onChange={e => onChange({...data, name: e.target.value})} 
          placeholder={data.type === 'LEGAL_ENTITY' ? 'ООО "Ромашка"' : 'Иванов Иван Иванович'}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             <Input label={`ИНН (${data.type === 'LEGAL_ENTITY' ? 10 : 12} цифр)`} inputMode="numeric" maxLength={data.type === 'LEGAL_ENTITY' ? 10 : 12} value={data.inn} onChange={e => onChange({...data, inn: e.target.value.replace(/\D/g, '')})} />
             
             {data.type === 'LEGAL_ENTITY' ? (
                 <>
                     <Input label="КПП" value={data.kpp} onChange={e => onChange({...data, kpp: e.target.value})} />
                     <Input label="ОГРН" value={data.ogrn} onChange={e => onChange({...data, ogrn: e.target.value})} />
                 </>
             ) : (
                 <>
                     <Input label="ОГРНИП (15 цифр)" inputMode="numeric" maxLength={15} value={data.ogrnip} onChange={e => onChange({...data, ogrnip: e.target.value.replace(/\D/g, '')})} />
                     <Input label="Дата регистрации ИП" type="date" value={data.registrationDate || ''} onChange={e => onChange({...data, registrationDate: e.target.value || null})} />
                 </>
             )}
             <Input label={`Следующий № счёта-фактуры в ${new Date().getFullYear()} г.`} type="number" min={1}
                 value={data.vatInvoiceNextYear === new Date().getFullYear() ? (data.vatInvoiceNextNumber ?? '') : ''}
                 placeholder="по порядку" title="При переходе с 1С: номер, с которого продолжить. С 1 января нумерация снова с 1"
                 onChange={e => onChange({...data, vatInvoiceNextNumber: e.target.value ? Number(e.target.value) : null,
                                                   vatInvoiceNextYear: e.target.value ? new Date().getFullYear() : null})} />
        </div>

        {data.type === 'LEGAL_ENTITY' && (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                 <Input label="Генеральный Директор" value={data.directorName} onChange={e => onChange({...data, directorName: e.target.value})} />
                 <Input label="ИНН владельца (для «внесения собственных средств»)" inputMode="numeric" value={data.ownerInn || ''} onChange={e => onChange({...data, ownerInn: e.target.value.replace(/\D/g, '')})} />
             </div>
        )}
        
        <div className="border-t border-slate-100 my-2"></div>
        
        <Input label={data.type === 'LEGAL_ENTITY' ? "Юридический адрес" : "Адрес регистрации"} value={data.legalAddress} onChange={e => onChange({...data, legalAddress: e.target.value})} />
        {data.type === 'LEGAL_ENTITY' && (
            <Input label="Фактический адрес" value={data.actualAddress} onChange={e => onChange({...data, actualAddress: e.target.value})} />
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
             <Select 
                label="Система налогообложения" 
                value={data.taxSystem} 
                onChange={e => onChange({...data, taxSystem: e.target.value})} 
                options={[
                    { value: 'ОСНО (НДС 22%)', label: 'ОСНО (Общая, с НДС)' }, 
                    { value: 'УСН 6%', label: 'УСН (Доходы 6%)' }, 
                    { value: 'УСН 15%', label: 'УСН (Доходы - Расходы 15%)' }
                ]} 
             />
             
             <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 mt-1">
                <div className="flex items-start gap-2">
                    <input 
                        type="checkbox" 
                        id={isEditing ? "isMspEdit" : "isMspNew"} 
                        className="mt-1 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        checked={data.isMsp || false}
                        onChange={e => onChange({...data, isMsp: e.target.checked})}
                    />
                    <div>
                        <label htmlFor={isEditing ? "isMspEdit" : "isMspNew"} className="text-sm font-bold text-blue-900 cursor-pointer">Входим в реестр МСП</label>
                        <p className="text-xs text-blue-700 mt-1">
                            Пониженный тариф: 15% с выплат сверх 1,5 МРОТ в месяц. С 2026 года — только для приоритетных отраслей:
                            основной ОКВЭД из перечня (распоряжение Правительства № 4125-р) и от него не меньше 70% доходов.
                        </p>
                        {data.isMsp && (
                            <label className="flex items-center gap-2 mt-2 text-xs text-blue-900 cursor-pointer">
                                <input type="checkbox" checked={data.mspPriority || false} onChange={e => onChange({...data, mspPriority: e.target.checked})} />
                                Приоритетная отрасль (тариф МСП с 2026 года)
                            </label>
                        )}
                    </div>
                </div>
             </div>
        </div>

        <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Выплата зарплаты</label>
            <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-500">Аванс за текущий месяц —</span>
                <input type="number" min={1} max={31} value={data.advanceDay ?? 25} onChange={e => onChange({...data, advanceDay: Number(e.target.value)})}
                    className="w-16 rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                <span className="text-slate-500">числа, расчёт за прошлый месяц —</span>
                <input type="number" min={1} max={31} value={data.settlementDay ?? 10} onChange={e => onChange({...data, settlementDay: Number(e.target.value)})}
                    className="w-16 rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
                <span className="text-slate-500">числа</span>
            </div>
            <div className="w-full md:w-2/3">
                <Select label="Округление начисленной зарплаты (из суммы на руки)" value={data.payrollRounding || 'HALF_UP'}
                    onChange={e => onChange({...data, payrollRounding: e.target.value})} options={[
                        { value: 'HALF_UP', label: 'Математическое (114 942,53 → 114 943)' },
                        { value: 'DOWN', label: 'Вниз (114 942,53 → 114 942)' },
                    ]} />
                <p className="text-xs text-slate-500 mt-1">НДФЛ всегда в целых рублях. Сверьте способ с бухгалтером — от него зависит НДФЛ на рубли.</p>
            </div>
            <label className="w-full md:w-2/3 flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="mt-1" checked={!!data.edoEnabled} onChange={e => onChange({...data, edoEnabled: e.target.checked})} />
                <span><span className="font-medium">ЭДО с самозанятыми</span> — задания и акты подписываются в «LiЧате» кодом из письма на email
                    исполнителя. Выключено — документы печатаются и подписываются на бумаге; загруженный скан считается подписанием.</span>
            </label>
            <div className="w-full md:w-2/3">
                <Select label="Нумерация счетов на оплату" value={data.invoiceNumbering || 'ORG_ACCOUNT'}
                    onChange={e => onChange({...data, invoiceNumbering: e.target.value})} options={[
                        { value: 'ORG_ACCOUNT', label: 'Своя серия на каждый расчётный счёт (Сбер №7, 8… и Т-Банк №22083…)' },
                        { value: 'ORG', label: 'Одна серия на организацию' },
                        { value: 'ORG_COUNTERPARTY', label: 'Своя серия на каждого контрагента' },
                    ]} />
                <p className="text-xs text-slate-500 mt-1">Номер подставляется следующий в серии; при выставлении счёта его можно исправить вручную (для переноса старых счетов).</p>
            </div>
        </div>

        {data.type === 'ENTREPRENEUR' && data.taxSystem?.includes('УСН 6%') && (
            <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" className="mt-1" checked={data.usnReduce1pct !== false}
                    onChange={e => onChange({...data, usnReduce1pct: e.target.checked})} />
                <span>Уменьшать авансы УСН на 1% взносов с дохода текущего года
                    <span className="block text-xs text-slate-500">1% платится до 1 июля следующего года, но по начислению может уменьшать авансы уже в этом году (так считает бухгалтер в декларации). Если выключить — 1% учтётся только в налоге за год.</span>
                </span>
            </label>
        )}
        {data.taxSystem?.includes('УСН') && (
            <VatModesEditor modes={data.vatModes || []} onChange={vatModes => onChange({...data, vatModes})} />
        )}

        <InjuriesRatesEditor rates={data.injuriesRates || []} onChange={injuriesRates => onChange({...data, injuriesRates})} />
    </div>
);

export const Profile: React.FC = () => {
  const { state, updateOrganization, addOrganization, deleteOrganization, addUser, deleteUser, showNotification } = useData();
  const { user: currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'company' | 'hr' | 'bank' | 'analysis' | 'users'>(() => searchParams.get('tab') === 'analysis' ? 'analysis' : searchParams.get('tab') === 'hr' ? 'hr' : 'company');

  // Какую организацию редактируем: выбранную в шапке, а в режиме «Все организации» — выбранную здесь
  const [editOrgId, setEditOrgId] = useState(state.currentOrganizationId);
  useEffect(() => { setEditOrgId(state.currentOrganizationId); }, [state.currentOrganizationId]);
  const activeOrg = state.organizations.find(o => o.id === editOrgId) || state.organizations[0];
  const [walletEdit, setWalletEdit] = useState<BankAccount | null>(null);
  
  const [companyForm, setCompanyForm] = useState<CompanyProfile>(
      activeOrg || { 
          id: '', name: '', type: 'LEGAL_ENTITY', 
          inn: '', kpp: '', ogrn: '', ogrnip: '', 
          legalAddress: '', actualAddress: '', directorName: '', 
          taxSystem: 'ОСНО (НДС 22%)', accounts: [], activeAccountId: null, isMsp: false, mspPriority: false, advanceDay: 25, settlementDay: 10, payrollRounding: 'HALF_UP', injuriesRates: [], vatModes: [] 
      }
  );
  
  // Обновляем форму при смене организации (но аккуратно, чтобы не сбрасывать ввод)
  useEffect(() => {
    if (activeOrg && activeOrg.id !== companyForm.id) {
        setCompanyForm({ ...activeOrg, type: activeOrg.type || 'LEGAL_ENTITY', isMsp: activeOrg.isMsp || false });
    }
  }, [activeOrg]);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: UserRole.MANAGER });
  
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [newBank, setNewBank] = useState<BankAccount>({ id: '', bankName: '', bik: '', accountNumber: '', correspondentAccount: '' });
  
  const [isNewOrgModalOpen, setIsNewOrgModalOpen] = useState(false);
  const [newOrgForm, setNewOrgForm] = useState<Partial<CompanyProfile>>({ type: 'LEGAL_ENTITY', name: '', taxSystem: 'ОСНО (НДС 22%)' });

  const handleCompanySave = (e: React.FormEvent) => {
    e.preventDefault();
    updateOrganization(companyForm);
    showNotification('Данные организации обновлены', 'success');
  };

  const handleCreateOrg = (e: React.FormEvent) => {
      e.preventDefault();
      if (!newOrgForm.name) return;
      addOrganization({
          name: newOrgForm.name || 'Новая компания',
          type: newOrgForm.type as 'LEGAL_ENTITY' | 'ENTREPRENEUR',
          inn: newOrgForm.inn || '',
          kpp: newOrgForm.kpp || '',
          ogrn: newOrgForm.ogrn || '',
          ogrnip: newOrgForm.ogrnip || '',
          legalAddress: newOrgForm.legalAddress || '',
          actualAddress: newOrgForm.actualAddress || '',
          directorName: newOrgForm.directorName || '',
          taxSystem: newOrgForm.taxSystem || 'ОСНО (НДС 22%)',
          accounts: [], activeAccountId: null, isMsp: false, mspPriority: false, advanceDay: 25, settlementDay: 10, payrollRounding: 'HALF_UP', injuriesRates: [], vatModes: []
      });
      setIsNewOrgModalOpen(false);
      setNewOrgForm({ type: 'LEGAL_ENTITY', name: '', taxSystem: 'ОСНО (НДС 22%)' });
      showNotification('Организация создана', 'success');
  };

  const handleDeleteCurrentOrg = () => {
      if (activeOrg) {
          deleteOrganization(activeOrg.id);
      }
  };

  const handleAddUser = (e: React.FormEvent) => {
    e.preventDefault();
    addUser(newUser);
    setIsUserModalOpen(false);
    setNewUser({ name: '', email: '', password: '', role: UserRole.MANAGER });
  };

  const handleAddBank = (e: React.FormEvent) => {
      e.preventDefault();
      const accountToAdd = { ...newBank, id: `ba_${Date.now()}`, kind: 'bank' as const };
      const updatedAccounts = [...(companyForm.accounts || []), accountToAdd];
      const updatedProfile = { ...companyForm, accounts: updatedAccounts, activeAccountId: companyForm.activeAccountId || accountToAdd.id };
      setCompanyForm(updatedProfile);
      updateOrganization(updatedProfile);
      setIsBankModalOpen(false);
      setNewBank({ id: '', bankName: '', bik: '', accountNumber: '', correspondentAccount: '' });
  };

  const handleDeleteBank = async (id: string) => {
      if (!(await confirmDialog('Удалить этот счёт?'))) return;
      const updatedAccounts = companyForm.accounts.filter(a => a.id !== id);
      let newActiveId = companyForm.activeAccountId;
      if (id === newActiveId) { newActiveId = updatedAccounts.length > 0 ? updatedAccounts[0].id : null; }
      const updatedProfile = { ...companyForm, accounts: updatedAccounts, activeAccountId: newActiveId };
      setCompanyForm(updatedProfile);
      updateOrganization(updatedProfile);
  };

  const handleAddCash = () => {
      const surname = companyForm.type === 'ENTREPRENEUR' ? companyForm.name.split(' ')[0] : companyForm.name;
      const cash: BankAccount = { id: `cash_${Date.now()}`, kind: 'cash', name: `Наличные — ${surname}`, bankName: '', bik: '',
                                  accountNumber: '', correspondentAccount: '', openingBalance: 0 };
      setWalletEdit(cash);
  };

  const saveWallet = (w: BankAccount) => {
      const exists = companyForm.accounts.some(a => a.id === w.id);
      const accounts = exists ? companyForm.accounts.map(a => a.id === w.id ? w : a) : [...companyForm.accounts, w];
      const updatedProfile = { ...companyForm, accounts };
      setCompanyForm(updatedProfile);
      updateOrganization(updatedProfile);
      setWalletEdit(null);
  };

  const handleSetActiveBank = (id: string) => {
      const updatedProfile = { ...companyForm, activeAccountId: id };
      setCompanyForm(updatedProfile);
      updateOrganization(updatedProfile);
      showNotification('Основной счет изменен', 'success');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-900">Настройки и Профиль</h1>
          <Button variant="outline" onClick={() => setIsNewOrgModalOpen(true)}><Plus className="mr-2 h-4 w-4" /> Добавить организацию</Button>
      </div>

      <div className="flex border-b border-slate-200">
        <button className={`py-3 px-6 font-medium text-sm border-b-2 transition-colors ${activeTab === 'company' ? 'border-accent text-accent' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('company')}>Реквизиты организации</button>
        <button className={`py-3 px-6 font-medium text-sm border-b-2 transition-colors ${activeTab === 'hr' ? 'border-accent text-accent' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('hr')}>Кадры</button>
        {currentUser?.role === UserRole.ADMIN && (<button className={`py-3 px-6 font-medium text-sm border-b-2 transition-colors ${activeTab === 'bank' ? 'border-accent text-accent' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('bank')}>Банковские выписки</button>)}
        {currentUser?.role === UserRole.ADMIN && (<button className={`py-3 px-6 font-medium text-sm border-b-2 transition-colors ${activeTab === 'analysis' ? 'border-accent text-accent' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('analysis')}>Анализ ИИ</button>)}
        {currentUser?.role === UserRole.ADMIN && (<button className={`py-3 px-6 font-medium text-sm border-b-2 transition-colors ${activeTab === 'users' ? 'border-accent text-accent' : 'border-transparent text-slate-500 hover:text-slate-700'}`} onClick={() => setActiveTab('users')}>Управление пользователями</button>)}
      </div>

      {activeTab === 'company' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
                <Card title="Основные данные организации">
                    <form onSubmit={handleCompanySave} className="space-y-4">
                        {state.organizations.length > 1 && (
                            <Select label="Организация" value={activeOrg?.id || ''} onChange={e => setEditOrgId(e.target.value)}
                                options={state.organizations.map(o => ({ value: o.id, label: orgDisplayName(o) }))} />
                        )}
                        <OrgFields data={companyForm} onChange={setCompanyForm} isEditing={true} />
                        <div className="flex justify-between pt-4 border-t border-slate-100">
                            {/* КНОПКА УДАЛЕНИЯ ОРГАНИЗАЦИИ */}
                            <Button type="button" variant="danger" onClick={handleDeleteCurrentOrg} className="bg-red-50 text-red-600 border-red-200 hover:bg-red-100"><Trash2 className="mr-2 h-4 w-4"/> Удалить организацию</Button>
                            <Button type="submit" variant="success"><Save className="mr-2 h-4 w-4" /> Сохранить изменения</Button>
                        </div>
                    </form>
                </Card>
            </div>

            <div className="space-y-6">
                {currentUser?.role === UserRole.ADMIN && activeOrg && <FacsimileCard org={activeOrg} />}
                <Card title="Счета и кассы">
                    <div className="space-y-4">
                        <div className="text-xs text-slate-500 mb-2">Основной счёт подставляется в операции по умолчанию. Остаток на начало — деньги до первой операции в приложении («остаток с 2025»).</div>
                        {(!companyForm.accounts || companyForm.accounts.length === 0) && (<div className="text-sm text-center py-4 text-slate-400 border border-dashed rounded-lg bg-slate-50">Нет счетов и касс</div>)}
                        {(companyForm.accounts || []).map(acc => {
                            const isActive = acc.id === companyForm.activeAccountId;
                            const cash = acc.kind === 'cash';
                            return (
                                <div key={acc.id} className={`p-3 rounded-lg border flex flex-col gap-2 relative transition-all ${isActive ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white border-slate-200 hover:border-blue-200'}`}>
                                    <div className="flex justify-between items-start"><div className="flex items-center gap-2 font-semibold text-slate-800 text-sm">{cash ? <Wallet size={16} className="text-emerald-600" /> : <Building2 size={16} className={isActive ? 'text-blue-600' : 'text-slate-400'} />}{cash ? (acc.name || 'Наличные') : acc.bankName}</div>{isActive && <Badge variant="success">Основной</Badge>}</div>
                                    {!cash && <div className="text-xs text-slate-600 font-mono space-y-1"><p>Р/С: {acc.accountNumber}</p><p>БИК: {acc.bik}</p></div>}
                                    <div className="text-xs text-slate-600">Остаток на начало: <strong>{formatMoney(acc.openingBalance || 0)}</strong>{acc.openingDate ? ` на ${formatDay(acc.openingDate)}` : ''}</div>
                                    <div className="flex gap-3 mt-1 pt-2 border-t border-slate-100/50">
                                        {!isActive && !cash && (<button onClick={() => handleSetActiveBank(acc.id)} className="text-xs flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"><CheckCircle size={12} /> Основной</button>)}
                                        <button onClick={() => setWalletEdit(acc)} className="text-xs text-slate-500 hover:text-blue-600">Остаток / название</button>
                                        <button onClick={() => handleDeleteBank(acc.id)} className="text-xs flex items-center gap-1 text-red-400 hover:text-red-600 ml-auto"><Trash2 size={12} /> Удалить</button>
                                    </div>
                                </div>
                            );
                        })}
                        <div className="grid grid-cols-2 gap-2">
                            <Button variant="outline" size="sm" onClick={() => setIsBankModalOpen(true)} className="border-slate-300 text-slate-500"><Plus className="mr-1 h-4 w-4" /> Счёт</Button>
                            <Button variant="outline" size="sm" onClick={handleAddCash} className="border-slate-300 text-slate-500"><Plus className="mr-1 h-4 w-4" /> Касса</Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
      )}

      {activeTab === 'hr' && activeOrg && (
        <div className="space-y-3">
          {state.organizations.length > 1 && (
            <div className="max-w-sm"><Select label="Организация" value={activeOrg.id} onChange={e => setEditOrgId(e.target.value)}
                options={state.organizations.map(o => ({ value: o.id, label: orgDisplayName(o) }))} /></div>
          )}
          <OrgHr orgId={activeOrg.id} />
        </div>
      )}
      {activeTab === 'bank' && currentUser?.role === UserRole.ADMIN && <BankStatements />}
      {activeTab === 'analysis' && currentUser?.role === UserRole.ADMIN && <Analysis />}
      {activeTab === 'users' && currentUser?.role === UserRole.ADMIN && (
        <div className="space-y-4">
             <div className="flex justify-end"><Button onClick={() => setIsUserModalOpen(true)}><UserPlus className="mr-2 h-4 w-4" /> Добавить пользователя</Button></div>
             <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                    <thead className="text-xs text-slate-700 uppercase bg-slate-50"><tr><th className="px-6 py-3">Имя</th><th className="px-6 py-3">Email</th><th className="px-6 py-3">Роль</th><th className="px-6 py-3 text-right">Действия</th></tr></thead>
                    <tbody>{state.users.map(u => (<tr key={u.id} className="border-b hover:bg-slate-50"><td className="px-6 py-4 font-medium text-slate-900 flex items-center gap-2"><div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">{u.name.charAt(0)}</div>{u.name}</td><td className="px-6 py-4">{u.email}</td><td className="px-6 py-4"><span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${u.role === UserRole.ADMIN ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>{u.role === UserRole.ADMIN ? <Shield size={10} /> : <User size={10} />}{u.role === UserRole.ADMIN ? 'Администратор' : 'Менеджер'}</span></td><td className="px-6 py-4 text-right">{u.id !== currentUser.id ? (<button onClick={() => deleteUser(u.id)} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>) : (<span className="text-xs text-slate-400 italic">Текущий</span>)}</td></tr>))}</tbody>
                </table>
             </div>
        </div>
      )}

      <Modal isOpen={isUserModalOpen} onClose={() => setIsUserModalOpen(false)} title="Новый пользователь">
         <form onSubmit={handleAddUser} className="space-y-4"><Input label="Имя" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} required /><Input label="Email (Логин)" type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} required /><Input label="Пароль (не короче 8 символов)" type="password" minLength={8} autoComplete="new-password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required /><Select label="Роль" value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value as UserRole})} options={[{ value: UserRole.MANAGER, label: 'Менеджер (Ограниченный доступ)' }, { value: UserRole.ADMIN, label: 'Администратор (Полный доступ)' }]} /><div className="flex justify-end gap-2 pt-4"><Button type="button" variant="secondary" onClick={() => setIsUserModalOpen(false)}>Отмена</Button><Button type="submit">Создать</Button></div></form>
      </Modal>
      <Modal isOpen={isBankModalOpen} onClose={() => setIsBankModalOpen(false)} title="Новый расчетный счет">
          <form onSubmit={handleAddBank} className="space-y-4"><Input label="Наименование банка" placeholder="ПАО Сбербанк" value={newBank.bankName} onChange={e => setNewBank({...newBank, bankName: e.target.value})} required /><Input label="БИК" value={newBank.bik} onChange={e => setNewBank({...newBank, bik: e.target.value})} required /><Input label="Номер счета" value={newBank.accountNumber} onChange={e => setNewBank({...newBank, accountNumber: e.target.value})} required /><Input label="Корреспондентский счет" value={newBank.correspondentAccount} onChange={e => setNewBank({...newBank, correspondentAccount: e.target.value})} /><div className="flex justify-end gap-2 pt-4"><Button type="button" variant="secondary" onClick={() => setIsBankModalOpen(false)}>Отмена</Button><Button type="submit">Добавить</Button></div></form>
      </Modal>
      <Modal isOpen={!!walletEdit} onClose={() => setWalletEdit(null)} title={walletEdit?.kind === 'cash' ? 'Касса' : 'Счёт'}>
          {walletEdit && (
              <form onSubmit={e => { e.preventDefault(); saveWallet(walletEdit); }} className="space-y-4">
                  {walletEdit.kind === 'cash' && <Input label="Название" value={walletEdit.name || ''} onChange={e => setWalletEdit({ ...walletEdit, name: e.target.value })} required />}
                  <Input label="Остаток на начало, ₽" type="number" step="0.01" value={walletEdit.openingBalance ?? 0} onChange={e => setWalletEdit({ ...walletEdit, openingBalance: Number(e.target.value) })} />
                  <Input label="На дату (для справки)" type="date" value={walletEdit.openingDate || ''} onChange={e => setWalletEdit({ ...walletEdit, openingDate: e.target.value || null })} />
                  <div className="flex justify-end gap-2 pt-2"><Button type="button" variant="secondary" onClick={() => setWalletEdit(null)}>Отмена</Button><Button type="submit">Сохранить</Button></div>
              </form>
          )}
      </Modal>
      <Modal isOpen={isNewOrgModalOpen} onClose={() => setIsNewOrgModalOpen(false)} title="Создание организации">
          <form onSubmit={handleCreateOrg} className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-700 mb-2">Будет создана новая, пустая рабочая область.</div>
              <OrgFields data={newOrgForm} onChange={setNewOrgForm} isEditing={false} />
              <div className="flex justify-end gap-2 pt-4"><Button type="button" variant="secondary" onClick={() => setIsNewOrgModalOpen(false)}>Отмена</Button><Button type="submit">Создать и переключиться</Button></div>
          </form>
      </Modal>
    </div>
  );
};